import { useState, useEffect } from "react";
import GumroadCheckout from "../components/GumroadCheckout";
import { getSubscription } from "../api";

/* ─── plan definitions ───────────────────────────────────────────────────── */
const PLANS = [
  {
    tier: "free",
    heading: "Starter",
    price: "$0",
    period: "forever",
    annualNote: null,
    bullets: [
      "5 AI posts per month",
      "1 Social account",
      "3 Proposals per month",
      "Basic image generation",
      "Job search across 14+ platforms",
      "Kronos Helper AI support",
      "Email support",
    ],
    cta: "Get Started Free",
    hue: 165,
    description: "Dip your toes in. No credit card needed.",
  },
  {
    tier: "hustler",
    heading: "Hustler",
    price: "$29.99",
    period: "/mo",
    annualNote: "$299/yr — save $60",
    popular: true,
    badge: "🔥 BEST VALUE",
    bullets: [
      "30 AI posts per month",
      "2 Social accounts connected",
      "20 AI proposals per month",
      "AI Receptionist (browser calls)",
      "Doc Studio documents (10/mo)",
      "HD image generation (10/mo)",
      "Job search across 14+ platforms",
      "Freelancer profile + CV upload",
      "Kronos Helper AI support",
      "Priority email support",
    ],
    cta: "Start Hustling — $29.99/mo",
    hue: 38,
    description: "Everything a solo freelancer needs to land clients daily. Just $1/day.",
  },
  {
    tier: "professional",
    heading: "Professional",
    price: "$79",
    period: "/mo",
    annualNote: "$790/yr — save $158",
    bullets: [
      "50 Posts per month",
      "3 Social accounts",
      "Unlimited proposals",
      "AI Receptionist + phone number",
      "HD image generation (20/mo)",
      "Video generation (5/mo)",
      "Doc Studio documents (unlimited)",
      "Multiple freelancer profiles",
      "Advanced analytics dashboard",
      "Priority support",
    ],
    cta: "Go Professional",
    hue: 291,
    description: "For serious freelancers ready to scale their business.",
  },
  {
    tier: "business",
    heading: "Studio",
    price: "$199",
    period: "/mo",
    annualNote: "$1,990/yr — save $398",
    bullets: [
      "200 Posts per month",
      "10 Social accounts",
      "Unlimited proposals",
      "AI Receptionist (1,000 min/mo)",
      "HD image generation (100/mo)",
      "Video generation (25/mo)",
      "Team collaboration (5 users)",
      "Client portal access",
      "Advanced analytics & reports",
      "24/7 priority support",
    ],
    cta: "Start Studio",
    hue: 200,
    description: "For agencies and studios managing multiple clients.",
  },
  {
    tier: "enterprise",
    heading: "Enterprise",
    price: "$499",
    period: "/mo",
    annualNote: "$4,990/yr — save $998",
    bullets: [
      "Unlimited everything",
      "Unlimited social accounts",
      "AI Receptionist (3,000 min/mo)",
      "4K image generation (unlimited)",
      "Video generation (unlimited)",
      "Team collaboration (15 users)",
      "White-label client portal",
      "Custom proposal templates",
      "Dedicated account manager",
      "24/7 VIP support + API access",
    ],
    cta: "Contact Sales",
    hue: 338,
    description: "Full-scale solution for large teams and enterprises.",
  },
];

/* ─── feature comparison rows ────────────────────────────────────────────── */
const FEATURES = [
  { icon: "🤖", title: "AI-Powered Content",   desc: "Generate engaging content with multiple AI models" },
  { icon: "🎨", title: "Image Generation",      desc: "Create stunning visuals with AI image generators" },
  { icon: "📅", title: "Smart Scheduling",      desc: "Schedule posts at optimal times automatically" },
  { icon: "📊", title: "Analytics",             desc: "Track performance across all platforms" },
  { icon: "📞", title: "AI Receptionist",       desc: "24/7 voice AI that handles calls for your business" },
  { icon: "✍️", title: "Proposal Writing",      desc: "AI-crafted proposals with architecture templates" },
  { icon: "🏗️", title: "Portfolio Manager",     desc: "Showcase your projects beautifully" },
  { icon: "📋", title: "Project Tracker",       desc: "Manage all projects from concept to completion" },
  { icon: "🔒", title: "Secure & Private",      desc: "Your data is encrypted and protected" },
  { icon: "⚡", title: "Lightning Fast",        desc: "Generate content in seconds, not hours" },
];

/* ─── component ──────────────────────────────────────────────────────────── */
export default function PricingTab() {
  const [currentSub, setCurrentSub] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    getSubscription()
      .then(({ data }) => setCurrentSub(data))
      .catch(() => {});
  }, []);

  const handleSuccess = (sub) => {
    setCurrentSub(sub);
    setSuccessMsg(`🎉 You're now on the ${sub.tier} plan! Enjoy your new features.`);
    setTimeout(() => setSuccessMsg(""), 6000);
  };

  return (
    <div className="space-y-8">

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div className="text-center py-4">
        <h1 className="text-3xl sm:text-4xl font-black text-white">Pricing</h1>
        <p className="text-gray-400 text-sm mt-1">Choose the perfect plan. All plans include core AI features with no hidden fees.</p>
        <div className="mt-4 inline-flex items-center gap-2 px-5 py-2 bg-green-600/20 border border-green-500/30 rounded-full text-green-400 font-bold text-sm">
          <span>🎉</span>
          <span>Start free, upgrade anytime. No credit card required!</span>
        </div>
      </div>

      {/* ── Success banner ───────────────────────────────────────────────── */}
      {successMsg && (
        <div className="text-center p-4 rounded-xl bg-teal-950/50 border border-teal-500/40 text-teal-100 font-semibold text-sm">
          {successMsg}
        </div>
      )}

      {/* ── Current plan badge ───────────────────────────────────────────── */}
      {currentSub && currentSub.tier !== "free" && (
        <div className="text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-600/20 border border-purple-500/30 text-purple-300 text-sm font-semibold">
            ✓ Current plan: <strong className="capitalize">{currentSub.tier}</strong>
            &nbsp;·&nbsp;{currentSub.usage?.posts?.used ?? 0}/{currentSub.usage?.posts?.limit ?? "∞"} posts used this month
          </span>
        </div>
      )}

      {/* ── Pricing cards ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
        {PLANS.map((plan) => (
          <PricingCard
            key={plan.tier}
            plan={plan}
            isCurrent={currentSub?.tier === plan.tier}
            onSuccess={handleSuccess}
          />
        ))}
      </div>

      {/* ── Payment Security & Trust ─────────────────────────────────────── */}
      <div className="py-6 space-y-6">
        {/* Trust badges row */}
        <div className="flex items-center justify-center gap-8 sm:gap-12 flex-wrap">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
            <span className="text-xs text-white font-medium">256-bit SSL</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <span className="text-xs text-white font-medium">PCI DSS</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            <span className="text-xs text-white font-medium">3D Secure</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
            <span className="text-xs text-white font-medium">200+ Countries</span>
          </div>
        </div>

        {/* Payment methods */}
        <div className="flex items-center justify-center gap-6 flex-wrap text-gray-500 text-xs">
          <span>Visa</span>
          <span>Mastercard</span>
          <span>PayPal</span>
          <span>Apple Pay</span>
          <span>Google Pay</span>
        </div>

        {/* Bottom line */}
        <p className="text-center text-[11px] text-gray-600">
          Cancel anytime · No lock-in · Instant activation · No hidden fees
        </p>
      </div>

      {/* ── Feature grid ─────────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-white/10 bg-[#0d1117] shadow-2xl shadow-black/40 overflow-hidden">
        <div className="px-5 py-4 border-b border-white/8 bg-black/30">
          <h2 className="text-white font-bold text-base">All Plans Include</h2>
        </div>
        <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {FEATURES.map((f, i) => {
            const hues = [210, 280, 160, 330, 200, 40, 300, 180, 140, 50];
            const hue = hues[i % hues.length];
            return (
              <div key={i} className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all duration-200 hover:scale-[1.02]"
                style={{ background: `linear-gradient(145deg, hsla(${hue},70%,50%,0.08) 0%, hsla(${hue},70%,50%,0.02) 100%)`, border: `1px solid hsla(${hue},70%,50%,0.15)` }}>
                <span className="text-2xl">{f.icon}</span>
                <span className="text-xs font-semibold text-white text-center">{f.title}</span>
                <span className="text-[10px] text-gray-500 text-center leading-tight">{f.desc}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <div className="text-center">
        <h3 className="text-lg font-bold text-white mb-2">Questions?</h3>
        <p className="text-gray-400 text-sm">
          Contact us at{" "}
          <a href="mailto:kronos@devxhouse.com" className="text-cyan-400 hover:text-cyan-300 font-semibold">
            kronos@devxhouse.com
          </a>
        </p>
      </div>
    </div>
  );
}

/* ─── PricingCard ────────────────────────────────────────────────────────── */
function PricingCard({ plan, isCurrent, onSuccess }) {
  const hsl = `${plan.hue},82%,51%`;

  return (
    <div
      className="relative flex flex-col rounded-2xl border p-6 transition-all duration-300 hover:scale-[1.02]"
      style={{
        background: isCurrent
          ? `hsla(${hsl},0.18)`
          : "rgba(43,43,43,0.5)",
        borderColor: isCurrent
          ? `hsl(${hsl})`
          : plan.popular
          ? `hsla(${hsl},0.6)`
          : "rgba(85,85,85,0.6)",
        boxShadow: isCurrent || plan.popular
          ? `0 0 24px hsla(${hsl},0.35)`
          : "none",
      }}
    >
      {/* Popular badge — inside card, no overflow clipping */}
      {plan.popular && (
        <div className="mb-3 flex justify-center">
          <span
            className="inline-block px-4 py-1 rounded-full text-xs font-bold text-white"
            style={{ background: `linear-gradient(to right, hsl(${hsl}), hsl(${plan.hue + 30},82%,51%))` }}
          >
            {plan.badge || "⭐ MOST POPULAR"}
          </span>
        </div>
      )}

      {/* Heading */}
      <h3
        className="text-xl font-black text-center mb-1"
        style={{ color: `hsl(${hsl})` }}
      >
        {plan.heading}
      </h3>

      {/* Description */}
      <p className="text-xs text-gray-400 text-center mb-4 leading-relaxed">{plan.description}</p>

      {/* Price */}
      <div className="text-center mb-1">
        <span className="text-4xl font-black text-white">{plan.price}</span>
        <span className="text-gray-400 text-sm">{plan.period}</span>
      </div>
      {plan.annualNote && (
        <p className="text-center text-xs text-teal-400 mb-4">{plan.annualNote}</p>
      )}
      {!plan.annualNote && <div className="mb-4" />}

      {/* Bullets */}
      <ul className="flex-1 space-y-2 mb-6">
        {plan.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
            <span style={{ color: `hsl(${hsl})` }} className="mt-0.5 font-bold flex-shrink-0">✓</span>
            {b}
          </li>
        ))}
      </ul>

      {/* CTA — Gumroad checkout */}
      <GumroadCheckout plan={plan} onSuccess={onSuccess} />
    </div>
  );
}
